import { ampli } from '@/ampli';
import type { RwaSwapInitiatedProperties } from '@/ampli';
import Decimal from 'decimal.js-light';
import { formatUnits, WalletClient } from 'viem';

import type { TokenUsdPrices } from '@/components/TransactionModule/tradeQuote';

import { NATIVE_TOKEN_DATA_ADDRESSES, NULL_ADDRESS } from '@/constants/addresses';

import type { UsdPriceInput } from '@/hooks/useUSDPrices';

import { type Issuer, type TokenResult, isRwaIssuer } from '@/utils/0x/token-registry.types';
import { CodexApiUsdPriceResponse, Price as TokenPrice } from '@/utils/codex.types';
import { trackError } from '@/utils/errors';
import { isTokenAddressNativeAsset } from '@/utils/multichain';
import { TokenInfo } from '@/utils/token.types';

import { Fee, Price, Quote } from '../../utils/0x.types';
import {
  IntentsApprovalTypes,
  IntentsSubmitApproval,
  IntentsSubmitTrade,
  IntentsTradeTypes,
} from '../../utils/intents/types';
import { EIP712TypedData, SignatureType, splitSignature } from '../../utils/signature';

// Native sentinels (matcha's ETH_FAKE_ADDRESS, the EVM null address) aren't
// real tokens to upstream price providers. Map them to the address providers
// index the native asset under, mirroring `getTokenPrices` on the lookup side.
const toPriceableAddress = (address: string, chainId: number): string =>
  address === NULL_ADDRESS || isTokenAddressNativeAsset(address, chainId)
    ? NATIVE_TOKEN_DATA_ADDRESSES[chainId]
    : address;

/**
 * Returns the token prices fetch params
 */
export const getPriceInputsForUsdPriceFetch = (
  sellTokenInfo?: TokenInfo,
  buyTokenInfo?: TokenInfo,
  sellTokenIssuer?: Issuer,
  buyTokenIssuer?: Issuer,
): UsdPriceInput[] => {
  const priceInputs: UsdPriceInput[] = [];

  if (sellTokenInfo) {
    const { chainId: networkId } = sellTokenInfo;
    const address = toPriceableAddress(sellTokenInfo.address, networkId);
    priceInputs.push({ address, networkId, issuer: sellTokenIssuer });

    if (address !== NATIVE_TOKEN_DATA_ADDRESSES[networkId]) {
      priceInputs.push({ address: NATIVE_TOKEN_DATA_ADDRESSES[networkId], networkId });
    }
  }

  if (buyTokenInfo) {
    const { chainId: networkId } = buyTokenInfo;
    const address = toPriceableAddress(buyTokenInfo.address, networkId);
    priceInputs.push({ address, networkId, issuer: buyTokenIssuer });

    if (address !== NATIVE_TOKEN_DATA_ADDRESSES[networkId]) {
      priceInputs.push({ address: NATIVE_TOKEN_DATA_ADDRESSES[networkId], networkId });
    }
  }

  return priceInputs;
};

/**
 * Returns the searched token price from the price response
 */
const findTokenPriceInPriceResponse = (
  tokenPrices: CodexApiUsdPriceResponse,
  address: string,
  chainId: number,
) => tokenPrices.find((price) => price && price.address === address && price.networkId === chainId);

/**
 * Returns the token prices for the sell token, buy token and native token
 */
export const getTokenPrices = (
  tokenPrices: CodexApiUsdPriceResponse,
  sellTokenInfo?: TokenInfo,
  buyTokenInfo?: TokenInfo,
): {
  nativeTokenPrice: TokenPrice | null | undefined;
  sellTokenPrice: TokenPrice | null | undefined;
  buyTokenPrice: TokenPrice | null | undefined;
} => ({
  nativeTokenPrice: sellTokenInfo?.chainId
    ? findTokenPriceInPriceResponse(
        tokenPrices,
        NATIVE_TOKEN_DATA_ADDRESSES[sellTokenInfo.chainId],
        sellTokenInfo.chainId,
      )
    : undefined,
  sellTokenPrice: sellTokenInfo
    ? findTokenPriceInPriceResponse(
        tokenPrices,
        toPriceableAddress(sellTokenInfo.address, sellTokenInfo.chainId),
        sellTokenInfo.chainId,
      )
    : undefined,
  buyTokenPrice: buyTokenInfo
    ? findTokenPriceInPriceResponse(
        tokenPrices,
        toPriceableAddress(buyTokenInfo.address, buyTokenInfo.chainId),
        buyTokenInfo.chainId,
      )
    : undefined,
});

interface CalculatePriceImpactParams {
  /** The amount of the sell token in its smallest unit. */
  sellAmount: string;
  /** The amount of the buy token in its smallest unit. */
  buyAmount: string;
  /** The sell token's metadata. */
  sellToken: TokenResult;
  /** The buy token's metadata. */
  buyToken: TokenResult;
  /** The current USD prices of the tokens. */
  tokenUsdPrices: TokenUsdPrices;
  /** The buy token's tax in basis points. */
  buyTaxBps: string | null;
  /** The sell token's tax in basis points. */
  sellTaxBps: string | null;
}

/**
 * Calculates the price impact while accounting for buy and sell taxes.
 * It returns a percentage and a dollar amount showing if you're getting
 * less or more value than expected after taxes.
 *
 * @param sellAmount - The amount of the token being sold, in its smallest units.
 * @param buyAmount - The amount of the token being purchased, in its smallest units.
 * @param sellToken - Details about the token being sold, including its decimals.
 * @param buyToken - Details about the token being purchased, including its decimals.
 * @param tokenUsdPrices - Current USD prices of the involved tokens.
 * @param buyTaxBps - Buy tax in basis points (100 bps = 1%).
 * @param sellTaxBps - Sell tax in basis points (100 bps = 1%).
 *
 * @returns An object with:
 * - `percent`: The price impact percentage.
 * - `usd`: The price impact in USD.
 *
 * If there's an error, it returns zero for both values.
 */
export function calculatePriceImpact({
  sellAmount,
  buyAmount,
  sellToken,
  buyToken,
  tokenUsdPrices,
  buyTaxBps,
  sellTaxBps,
}: CalculatePriceImpactParams) {
  if (!sellAmount || !buyAmount) {
    throw new Error('Invalid sell or buy amount passed to calculatePriceImpact.');
  }

  // Get the USD values for the tokens being sold and bought.
  const { buyTokenUsd, sellTokenUsd } = tokenUsdPrices;

  try {
    // Convert the raw amounts into normal numbers based on the token decimals.
    const formattedSellAmount = Number(formatUnits(BigInt(sellAmount), sellToken.decimals));
    const formattedBuyAmount = Number(formatUnits(BigInt(buyAmount), buyToken.decimals));

    // Find the total USD paid (sell side) and received (buy side).
    const sellTokenTotalUsd = sellTokenUsd * formattedSellAmount;
    const buyTokenTotalUsd = buyTokenUsd * formattedBuyAmount;

    // Basis points divisor (10000) is used to turn bps into a decimal fraction.
    // For example, 500 bps = 500/10000 = 0.05 = 5%.
    const BPS_DIVISOR = 10000;

    const sellTaxRate = sellTaxBps ? Number(sellTaxBps) / BPS_DIVISOR : 0;
    const buyTaxRate = buyTaxBps ? Number(buyTaxBps) / BPS_DIVISOR : 0;

    // taxFactor is the fraction of value kept after all taxes.
    // For example, a 5% sell tax and 5% buy tax results in keeping 0.95 * 0.95 = 0.9025.
    const taxFactor = (1 - sellTaxRate) * (1 - buyTaxRate);

    // actualPriceRatio is what you actually receive (USD) divided by what you pay (USD).
    // A ratio of 1 means no loss. Less than 1 means you got less value than you put in.
    const actualPriceRatio =
      buyTokenTotalUsd && sellTokenTotalUsd ? buyTokenTotalUsd / sellTokenTotalUsd : 0;

    // Compare actual ratio to taxFactor. If they match, there's no extra impact beyond taxes.
    // If actual is lower, price impact is negative.
    const priceImpactPercentage = taxFactor > 0 ? (actualPriceRatio / taxFactor - 1) * 100 : 0;

    // baselineUsd is what you'd expect after taxes if there were no other losses.
    const baselineUsd = sellTokenTotalUsd * taxFactor;

    // priceImpactUSD is how many USD you're short of (or over) the tax-adjusted expectation.
    const priceImpactUSD = buyTokenTotalUsd - baselineUsd;

    return {
      percent: priceImpactPercentage,
      usd: priceImpactUSD,
    };
  } catch (error) {
    if (error instanceof Error) {
      trackError(error);
    }
    return {
      percent: 0,
      usd: 0,
    };
  }
}

/**
 * Determines if a given string represents a valid slippage value.
 * A valid slippage value is a number that is greater than 0 and less than or equal to 100.
 *
 * The function trims the input string before validation to remove leading and trailing whitespace. It then checks
 * whether the trimmed string, when converted to a number, meets the criteria for being considered a valid slippage value.
 *
 * @param value - The input string representing a potential slippage value to validate.
 * @returns boolean, `true` if the input string is a valid slippage value according to the defined criteria; otherwise, `false`.
 *
 * @example
 * isValidSlippageText("0"),     // false, not greater than 0
 * isValidSlippageText("0.0"),   // false, not greater than 0
 * isValidSlippageText("0."),    // false, not valid decimal
 * isValidSlippageText(".0"),    // false, not valid decimal
 * isValidSlippageText("101"),   // false, exceeds maximum integer limit
 * isValidSlippageText("1x")     // false, contains non-numeric characters
 * isValidSlippageText("0.1"),   // true, valid decimal within range
 * isValidSlippageText("1"),     // true, valid within range
 * isValidSlippageText("100"),   // true, valid maximum integer
 */
export function isValidSlippageText(value: string) {
  const trimmedValue = value.trim();
  const slippage = Number(trimmedValue);
  if (isNaN(slippage)) return false;
  return slippage > 0 && slippage <= 100;
}

/** Token symbols longer than this are truncated in the trade module. */
export const LONG_TOKEN_SYMBOL_LENGTH = 10;

/**
 * Shallow router-query equality (string params by value, array params by
 * element). Used to skip URL writes that would not change the current route,
 * so no history churn or render loop can come from a redundant sync.
 */
export const isSameQuery = (
  a: Record<string, string | string[] | undefined>,
  b: Record<string, string | string[] | undefined>,
): boolean => {
  const aKeys = Object.keys(a);
  if (aKeys.length !== Object.keys(b).length) return false;
  return aKeys.every((key) => {
    const aValue = a[key];
    const bValue = b[key];
    if (Array.isArray(aValue) && Array.isArray(bValue)) {
      return aValue.length === bValue.length && aValue.every((value, i) => value === bValue[i]);
    }
    return aValue === bValue;
  });
};

/**
 * Adds two amounts in different tokens and converts them into a target currency using the specified exchange rate.
 *
 * @param amount1 - The first amount in smallest unit
 * @param decimals1 - The number of decimal places used in the first amount's currency.
 * @param amount2 - The second amount in smallest unit.
 * @param decimals2 - The number of decimal places used in the second amount's currency.
 * @param outputDecimals - The number of decimal places for the output amount.
 * @param exchangeRate - The exchange rate from the first currency to the second currency.
 * @returns The sum of the two amounts converted into the target currency, rounded to the nearest whole number.
 */
export function addConvertedAmounts({
  amount1,
  decimals1,
  amount2,
  decimals2,
  outputDecimals,
  exchangeRate,
}: {
  amount1: string;
  decimals1: number;
  amount2: string;
  decimals2: number;
  outputDecimals: number;
  exchangeRate: Decimal;
}) {
  const ten = new Decimal(10);

  // Convert amount1 to the output decimals
  // Step 1: Convert amount1 from its original decimals to a standard unit
  // Step 2: Apply the exchange rate to the converted amount
  // Step 3: Convert the standard unit to the desired output decimals
  const convertedAmount1 = new Decimal(amount1)
    .div(ten.pow(decimals1))
    .mul(exchangeRate)
    .mul(ten.pow(outputDecimals));

  // Convert amount2 to the output decimals
  // Step 1: Convert amount2 from its original decimals to a standard unit
  // Step 2: Apply the exchange rate to the converted amount
  // Step 3: Convert the exchanged amount to the desired output decimals
  const convertedAmount2 = new Decimal(amount2)
    .div(ten.pow(decimals2))
    .mul(exchangeRate)
    .mul(ten.pow(outputDecimals));

  // Add the two converted amounts
  return convertedAmount1.add(convertedAmount2).toFixed(0, Decimal.ROUND_HALF_EVEN);
}

/**
 * Calculates the exchange rate between two tokens based on their amounts and decimal places.
 *
 * @param buyAmount - The amount bought, in its smallest unit.
 * @param sellAmount - The amount sold, in its smallest unit.
 * @param buyDecimals - The number of decimal places used in the buy currency.
 * @param sellDecimals - The number of decimal places used in the sell currency.
 * @returns The amount of buy token per unit of sell token.

 */
export function calculateExchangeRate({
  buyAmount,
  sellAmount,
  buyDecimals,
  sellDecimals,
}: {
  buyAmount: string;
  sellAmount: string;
  buyDecimals: number;
  sellDecimals: number;
}) {
  const ten = new Decimal(10);
  const buyAmountDecimal = new Decimal(buyAmount).div(ten.pow(buyDecimals));
  const sellAmountDecimal = new Decimal(sellAmount).div(ten.pow(sellDecimals));

  if (sellAmountDecimal.isZero()) {
    return new Decimal(0);
  }

  return buyAmountDecimal.div(sellAmountDecimal);
}

/**
 * Calculates the gross buy amount including fees for a given price response.
 *
 * @param price - The price response object containing fee information and token amounts.
 * @param sellTokenDecimals - The number of decimal places used in the sell token.
 * @param buyTokenDecimals - The number of decimal places used in the buy token.
 * @returns The gross buy amount including fees, rounded to the nearest whole number.
 */
export function calculateGrossBuyAmount({
  priceOrQuote,
  sellTokenDecimals,
  buyTokenDecimals,
}: {
  priceOrQuote: Price | Quote;
  sellTokenDecimals: number;
  buyTokenDecimals: number;
}) {
  const { buyToken, sellToken, fees, sellAmount, buyAmount } = priceOrQuote;
  const { gasFee, zeroExFee, integratorFee } = fees;
  const feeEntries = [zeroExFee, gasFee, integratorFee].filter((fee): fee is Fee => fee !== null);

  if (feeEntries.length === 0) {
    return priceOrQuote.buyAmount;
  }

  let totalSellTokenFeeAmount = new Decimal('0');
  let totalBuyTokenFeeAmount = new Decimal('0');

  for (const fee of feeEntries) {
    if (fee.token === sellToken) {
      totalSellTokenFeeAmount = totalSellTokenFeeAmount.add(fee.amount);
      continue;
    }

    if (fee.token === buyToken) {
      totalBuyTokenFeeAmount = totalBuyTokenFeeAmount.add(fee.amount);
      continue;
    }

    return priceOrQuote.buyAmount;
  }

  const grossBuyAmountWithoutSellTokenFees = new Decimal(buyAmount).add(totalBuyTokenFeeAmount);

  if (totalSellTokenFeeAmount.isZero()) {
    return grossBuyAmountWithoutSellTokenFees.toFixed(0, Decimal.ROUND_HALF_EVEN);
  }

  const netSellAmount = new Decimal(sellAmount).sub(totalSellTokenFeeAmount);

  if (netSellAmount.lte(0)) {
    return priceOrQuote.buyAmount;
  }

  const exchangeRate = calculateExchangeRate({
    sellAmount: netSellAmount.toFixed(0, Decimal.ROUND_HALF_EVEN),
    buyAmount: grossBuyAmountWithoutSellTokenFees.toFixed(0, Decimal.ROUND_HALF_EVEN),
    sellDecimals: sellTokenDecimals,
    buyDecimals: buyTokenDecimals,
  });

  const sellTokenFeesInBuyToken = addConvertedAmounts({
    amount1: totalSellTokenFeeAmount.toFixed(0, Decimal.ROUND_HALF_EVEN),
    decimals1: sellTokenDecimals,
    amount2: '0',
    decimals2: buyTokenDecimals,
    outputDecimals: buyTokenDecimals,
    exchangeRate,
  });

  const grossBuyAmount = grossBuyAmountWithoutSellTokenFees.add(sellTokenFeesInBuyToken);

  if (grossBuyAmount.lte(0)) {
    return priceOrQuote.buyAmount;
  }

  return grossBuyAmount.toFixed(0, Decimal.ROUND_HALF_EVEN);
}

interface SignIntentsApprovalParams {
  type: IntentsApprovalTypes;
  eip712: EIP712TypedData;
  walletClient: WalletClient;
}

/**
 * The Intents API rejects the extra viem signature fields (recoveryParam,
 * _vs, yParityAndS, compact), so only the minimal {signatureType, v, r, s}
 * object is kept.
 */
export async function signIntentsApproval({
  type,
  eip712,
  walletClient,
}: SignIntentsApprovalParams): Promise<IntentsSubmitApproval> {
  if (!walletClient.account) {
    throw new Error('Wallet account not available');
  }

  const signature = await walletClient.signTypedData({
    account: walletClient.account,
    domain: eip712.domain,
    types: eip712.types,
    primaryType: eip712.primaryType,
    message: eip712.message,
  });
  const splitSig = splitSignature(signature);

  const signedApproval: IntentsSubmitApproval = {
    type,
    eip712,
    signature: {
      signatureType: SignatureType.EIP712,
      v: Number(splitSig.v),
      r: splitSig.r,
      s: splitSig.s,
    },
  };

  return signedApproval;
}

interface SignIntentsTradeParams {
  type: IntentsTradeTypes;
  eip712: EIP712TypedData;
  walletClient: WalletClient;
  isSmartWallet?: boolean;
}

/**
 * Signs an intents trade with the signature trimmed to the minimal object
 * the Intents API accepts. Smart wallets sign as raw bytes for EIP-1271
 * validation.
 */
export async function signIntentsTrade({
  type,
  eip712,
  walletClient,
  isSmartWallet,
}: SignIntentsTradeParams): Promise<IntentsSubmitTrade> {
  if (!walletClient.account) {
    throw new Error('Wallet account not available');
  }

  const signature = await walletClient.signTypedData({
    account: walletClient.account,
    domain: eip712.domain,
    types: eip712.types,
    primaryType: eip712.primaryType,
    message: eip712.message,
  });

  if (isSmartWallet) {
    return {
      type,
      eip712,
      signature: {
        signatureType: SignatureType.Raw,
        signatureBytes: signature,
      },
    };
  }

  const splitSig = splitSignature(signature);

  return {
    type,
    eip712,
    signature: {
      signatureType: SignatureType.EIP712,
      v: Number(splitSig.v),
      r: splitSig.r,
      s: splitSig.s,
    },
  };
}

/**
 * Fires an `rwa_swap_initiated` Amplitude event if either token is an RWA token.
 * Shared across market, limit, and cross-chain submit flows.
 */
export const trackRwaSwapInitiated = ({
  sellToken,
  buyToken,
  orderType,
  transactionHash,
}: {
  sellToken: { address: string; symbol: string; chainId: number; issuer?: Issuer | null };
  buyToken: { address: string; symbol: string; chainId: number; issuer?: Issuer | null };
  orderType: RwaSwapInitiatedProperties['swap_order_type'];
  transactionHash?: string;
}) => {
  const sellIsRwa = isRwaIssuer(sellToken.issuer ?? null);
  const buyIsRwa = isRwaIssuer(buyToken.issuer ?? null);
  if (!sellIsRwa && !buyIsRwa) return;

  const rwaToken = sellIsRwa ? sellToken : buyToken;
  ampli.rwaSwapInitiated({
    swap_order_type: orderType,
    rwa_token_address: rwaToken.address,
    rwa_token_symbol: rwaToken.symbol,
    rwa_token_side: sellIsRwa && buyIsRwa ? 'both' : sellIsRwa ? 'sell' : 'buy',
    chain_id: rwaToken.chainId,
    transaction_hash: transactionHash,
  });
};
